import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { Box, Paper, Typography, Drawer, IconButton, Toolbar, Avatar } from '@mui/material'
import { useSelector } from 'react-redux'
import { RootState, useAppSelector } from '@/redux/rootReducer'
import {
  useGetChatRoomQuery,
  useSendChatMessageMutation,
  useGetChatRoomsJoinedByUserQuery,
  useCreateChatRoomMutation,
  useLazyGetChatRoomMessagesQuery,
} from '@/core/services/chat-room'
import { useGetStaffsQuery, useLazyGetStaffsQuery } from '@/core/services/staff'
import { selectEstablishmentById } from '@/redux/reducers/establishment.reducer'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ChatRoom, ChatMessage } from '@/core/models/chat-room.model'
import { Staff } from '@/core/models/staff.model'
import { useConfirmReadingMessagesMutation } from '@/core/services/chat-room'
import { plural, t } from '@lingui/macro'
import _ from 'lodash'
import MenuIcon from '@mui/icons-material/Menu'
import AddIcon from '@mui/icons-material/Add'
import GroupsIcon from '@mui/icons-material/Groups'
import { ChatDrawer } from './ChatDrawer'
import { CreateGroupChat } from './CreateGroupChat'
import { Allotment } from 'allotment'
import 'allotment/dist/style.css'
import { useSnackbar } from 'notistack'
import { getApiErrorMessage } from '@/utils/errors'
import { ChatMessages } from '@/features/chat-messages'
import { useChatSocket } from '../model/chatSocket'
import { skipToken } from '@reduxjs/toolkit/query'
import { ChatLoader } from './ChatLoader'
import { ChatPlaceholder } from './ChatPlaceholder'
import { MAIN_HEADER_HEIGHT } from '@/layouts/main'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { ContactPickerModal } from './ContactPickerModal'

const defaultSize = [30, 70]

/**
 * Chat is open between colleagues of any role. Without an explicit status the staff query only
 * returns the *other* role (doctors saw nurses only), so a doctor could not message a doctor.
 */
const CHAT_CONTACT_STATUSES = { $in: ['MEDECIN', 'PDS'] }

const ChatPage = () => {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()

  const authUser = useSelector(selectAuthUser)
  const establishment = useAppSelector((state) => {
    if (!authUser || _.isUndefined(authUser.establishment)) return undefined
    if (typeof authUser.establishment !== 'string') return authUser.establishment
    return selectEstablishmentById(state, authUser.establishment)
  })

  const [searchParams] = useSearchParams()
  const chatRoomId = searchParams.get('id')
  const chatType = searchParams.get('type') as 'group' | 'private' | null

  const lastAuthorizedChatRoomId = useRef<string | null>(null)
  const messagesContainerRef = useRef<HTMLDivElement>(null)

  const [activeChatType, setActiveChatType] = useState<'group' | 'private'>(chatType || 'private')
  const [activeChatRoom, setActiveChatRoom] = useState<ChatRoom | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [isLoadingChat, setIsLoadingChat] = useState(false)
  const [mobileContactPickerOpen, setMobileContactPickerOpen] = useState(false)
  const [mobileGroupOpen, setMobileGroupOpen] = useState(false)
  const [mobileComposeAnchorEl, setMobileComposeAnchorEl] = useState<null | HTMLElement>(null)

  // Pagination state for contacts
  const [isLoadingMoreContacts, setIsLoadingMoreContacts] = useState(false)
  const CONTACTS_PAGE_SIZE = 20

  // Pagination state for chat rooms
  const CHATS_PAGE_SIZE = 50

  // Pagination state for messages
  const [messagePage, setMessagePage] = useState(0)
  const [isLoadingMoreMessages, setIsLoadingMoreMessages] = useState(false)
  const [accumulatedMessages, setAccumulatedMessages] = useState<ChatMessage[]>([])
  const MESSAGES_PAGE_SIZE = 50

  const {
    data: chatRoom,
    refetch: refetchChatRoom,
    isLoading: isChatRoomLoading,
  } = useGetChatRoomQuery(
    chatRoomId
      ? {
          chatRoomId,
          limit: MESSAGES_PAGE_SIZE,
          skip: 0,
        }
      : skipToken,
    { refetchOnMountOrArgChange: true }
  )
  const [loadMoreMessages] = useLazyGetChatRoomMessagesQuery()
  const { data: groupChats, refetch: refetchGroupChats } = useGetChatRoomsJoinedByUserQuery(
    {
      isGroup: true,
      limit: CHATS_PAGE_SIZE,
      skip: 0,
    },
    { refetchOnMountOrArgChange: false }
  )
  const { data: privateChats } = useGetChatRoomsJoinedByUserQuery(
    {
      isGroup: false,
      limit: CHATS_PAGE_SIZE,
      skip: 0,
    },
    { refetchOnMountOrArgChange: false }
  )

  // Paginated staff fetching
  const [additionalStaffs, setAdditionalStaffs] = useState<Staff[]>([])
  const { data: initialStaffs, isFetching: isFetchingInitialStaffs } = useGetStaffsQuery(
    establishment?._id
      ? {
          query: { establishment: establishment._id, active: true, status: CHAT_CONTACT_STATUSES },
          options: { limit: CONTACTS_PAGE_SIZE, skip: 0, orderBy: 'userInfo.name' },
        }
      : { query: {}, options: {} },
    { skip: !establishment?._id }
  )
  const [loadMoreStaffs] = useLazyGetStaffsQuery()

  // Accumulate all staff data (initial + additional pages) and filter out current user
  const staffs = useMemo(() => {
    const allStaffs = [...(initialStaffs?.items || []), ...additionalStaffs]
    const filteredStaffs = allStaffs.filter((staff) => staff._id !== authUser?._id)
    return filteredStaffs
  }, [initialStaffs?.items, additionalStaffs, authUser?._id])

  // Handle loading more contacts
  const handleLoadMoreContacts = useCallback(async () => {
    if (!establishment?._id || isLoadingMoreContacts || !initialStaffs?.totalCount) return

    const currentCount = staffs.length
    if (currentCount >= initialStaffs.totalCount) return

    setIsLoadingMoreContacts(true)
    try {
      const result = await loadMoreStaffs({
        query: { establishment: establishment._id, active: true, status: CHAT_CONTACT_STATUSES },
        options: { limit: CONTACTS_PAGE_SIZE, skip: currentCount, orderBy: 'userInfo.name' },
      }).unwrap()

      if (result?.items) {
        setAdditionalStaffs((prev) => [...prev, ...result.items])
      }
    } catch (error) {
      console.error('Error loading more contacts:', error)
    } finally {
      setIsLoadingMoreContacts(false)
    }
  }, [establishment?._id, isLoadingMoreContacts, initialStaffs?.totalCount, staffs.length, loadMoreStaffs])

  // Check if there are more contacts to load
  const hasMoreContacts = initialStaffs ? staffs.length < initialStaffs.totalCount : false

  // Reset accumulated messages when chat room changes
  useEffect(() => {
    if (chatRoomId) {
      setAccumulatedMessages([])
      setMessagePage(0)
    }
  }, [chatRoomId])

  // Combine initial messages with accumulated messages
  const displayedMessages = useMemo(() => {
    if (!chatRoom?.messages) return []
    return [...chatRoom.messages, ...accumulatedMessages]
  }, [chatRoom?.messages, accumulatedMessages])

  // Handle loading more messages
  const handleLoadMoreMessages = useCallback(async () => {
    if (!chatRoomId || isLoadingMoreMessages) return

    setIsLoadingMoreMessages(true)
    const nextPage = messagePage + 1

    try {
      const result = await loadMoreMessages({
        chatRoomId,
        limit: MESSAGES_PAGE_SIZE,
        skip: nextPage * MESSAGES_PAGE_SIZE,
      }).unwrap()

      if (result?.messages && result.messages.length > 0) {
        setAccumulatedMessages((prev) => [...prev, ...result.messages])
        setMessagePage(nextPage)
      }
    } catch (error) {
      console.error('Error loading more messages:', error)
    } finally {
      setIsLoadingMoreMessages(false)
    }
  }, [chatRoomId, isLoadingMoreMessages, messagePage, loadMoreMessages])

  // Check if there are more messages to load
  const hasMoreMessages = chatRoom ? chatRoom.messages.length >= MESSAGES_PAGE_SIZE || accumulatedMessages.length > 0 : false
  const [confirmReadingMessages] = useConfirmReadingMessagesMutation()
  const [createChatRoom] = useCreateChatRoomMutation()
  const [sendChatMessage] = useSendChatMessageMutation()

  useChatSocket(groupChats ?? [], privateChats ?? [], activeChatRoom, setActiveChatRoom)

  useEffect(() => {
    if (chatRoomId && !chatRoom && isChatRoomLoading) {
      setIsLoadingChat(true)
    } else if (chatRoom && chatRoomId) {
      setActiveChatRoom(chatRoom)
      setIsLoadingChat(false)

      if (chatType) {
        setActiveChatType(chatType)
      } else if (chatRoom.isPrivate) {
        setActiveChatType('private')
      } else {
        setActiveChatType('group')
      }

      if (lastAuthorizedChatRoomId.current === chatRoomId) {
        lastAuthorizedChatRoomId.current = null

        setTimeout(() => {
          refetchChatRoom()
        }, 200)
      }
    } else if (!chatRoomId) {
      setActiveChatRoom(null)
      setIsLoadingChat(false)
      if (chatType) {
        setActiveChatType(chatType)
      }
    }
  }, [chatRoom, chatRoomId, chatType, refetchChatRoom, isChatRoomLoading])

  useEffect(() => {
    const element = messagesContainerRef.current
    if (element) {
      element.scrollTop = element.scrollHeight
    }
  }, [chatRoom, messagesContainerRef])

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen)
  }

  const handleSelectGroup = async (group: ChatRoom) => {
    const targetUrl = `/chat?type=group&id=${group._id}`
    trackedNavigate(targetUrl)
    setDrawerOpen(false)

    if (authUser?._id) {
      await confirmReadingMessages(group._id)
    }
  }

  const handleStartPrivateChat = async (staff: Staff) => {
    try {
      const users = [staff._id, authUser?._id!]
      const room = await createChatRoom({ users, createdBy: authUser?._id!, isPrivate: true }).unwrap()

      const targetUrl = `/chat?type=private&id=${room._id}`
      trackedNavigate(targetUrl)
      setDrawerOpen(false)

      if (authUser?._id) {
        await confirmReadingMessages(room._id)
      }
    } catch (error) {
      console.error('Error creating private chat:', error)
    }
  }

  const handleGroupCreated = () => {
    refetchGroupChats()
  }

  const hasUnreadMessages = useCallback(
    (chatRoom: ChatRoom): boolean => {
      if (!chatRoom?.messages || !authUser?._id) return false

      return chatRoom.messages.some((message) => {
        const isFromOtherUser = message.author !== authUser._id
        const isNotReadByMe = !(message.readBy || []).includes(authUser._id)
        return isFromOtherUser && isNotReadByMe
      })
    },
    [authUser?._id]
  )

  const handleSendMessage = async (message: string) => {
    if (!activeChatRoom) return
    try {
      await sendChatMessage({ chatRoomId: activeChatRoom._id, message }).unwrap()
    } catch (error: any) {
      enqueueSnackbar(getApiErrorMessage(error.message, t`An error occurred while sending the message`), {
        variant: 'error',
        autoHideDuration: 3000,
      })
    }
  }

  const trackedNavigate = (url: string) => {
    const match = url.match(/id=([^&]+)/)
    if (match) {
      const chatRoomId = match[1]
      lastAuthorizedChatRoomId.current = chatRoomId
      setIsLoadingChat(true)
      setActiveChatRoom(null)
    }
    navigate(url, { replace: false })
  }

  const handleMarkAsRead = async (chatRoomId: string) => {
    await confirmReadingMessages(chatRoomId)
  }

  const handleStartNewChat = async (staffId: string) => {
    if (!authUser) return
    try {
      const users = [staffId, authUser._id!]
      const room = await createChatRoom({ users, createdBy: authUser._id!, isPrivate: true }).unwrap()

      const targetUrl = `/chat?type=private&id=${room._id}`
      trackedNavigate(targetUrl)
      setDrawerOpen(false)

      await confirmReadingMessages(room._id)
    } catch (error) {
      console.error('Error creating private chat:', error)
    }
  }

  const getChatTitle = (chatRoom: ChatRoom) => {
    if (activeChatType === 'private') {
      const otherUserId = chatRoom.users.find((u: Partial<Staff>) => u._id !== authUser?._id)?._id
      const staff = staffs?.find((s) => s._id === otherUserId)
      return staff ? `${staff.userInfo.name} ${staff.userInfo.surname}`.trim() : ''
    }

    return chatRoom.name
  }

  const getChatSubtitle = (chatRoom: ChatRoom) => {
    if (activeChatType === 'private') {
      const otherUserId = chatRoom.users.find((u: Partial<Staff>) => u._id !== authUser?._id)?._id
      const staff = staffs?.find((s) => s._id === otherUserId)
      return staff?.speciality || ''
    }
    return plural(chatRoom.users.length, {
      0: '# member',
      one: '# member',
      other: '# members',
    })
  }

  return (
    <Box sx={{ height: `calc(100vh - ${MAIN_HEADER_HEIGHT}px)`, display: 'flex' }}>
      <Drawer
        variant="temporary"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: '85vw',
            maxWidth: 400,
          },
        }}
      >
        <ChatDrawer
          staffs={staffs}
          privateChats={privateChats ?? []}
          groupChats={groupChats ?? []}
          handleStartPrivateChat={handleStartPrivateChat}
          handleGroupCreated={handleGroupCreated}
          handleSelectGroup={handleSelectGroup}
          hasUnreadMessages={hasUnreadMessages}
          activeChatRoom={activeChatRoom}
          trackedNavigate={trackedNavigate}
          handleStartNewChat={handleStartNewChat}
          onLoadMoreContacts={handleLoadMoreContacts}
          isLoadingMoreContacts={isLoadingMoreContacts}
          hasMoreContacts={hasMoreContacts}
        />
      </Drawer>

      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: { xs: 'none', sm: 'flex' },
        }}
      >
        <Allotment defaultSizes={defaultSize}>
          <Allotment.Pane minSize={250} maxSize={600}>
            <ChatDrawer
              staffs={staffs}
              privateChats={privateChats ?? []}
              groupChats={groupChats ?? []}
              handleStartPrivateChat={handleStartPrivateChat}
              handleGroupCreated={handleGroupCreated}
              handleSelectGroup={handleSelectGroup}
              hasUnreadMessages={hasUnreadMessages}
              activeChatRoom={activeChatRoom}
              trackedNavigate={trackedNavigate}
              handleStartNewChat={handleStartNewChat}
              onLoadMoreContacts={handleLoadMoreContacts}
              isLoadingMoreContacts={isLoadingMoreContacts}
              hasMoreContacts={hasMoreContacts}
            />
          </Allotment.Pane>

          <Allotment.Pane minSize={400}>
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: '#f5f5f5',
              }}
            >
              {activeChatRoom ? (
                <>
                  <Box
                    sx={{
                      p: 2,
                      borderBottom: '1px solid #e0e0e0',
                      backgroundColor: 'white',
                      flexShrink: 0,
                    }}
                  >
                    <Typography variant="h6">{getChatTitle(activeChatRoom)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {getChatSubtitle(activeChatRoom)}
                    </Typography>
                  </Box>

                  <Box sx={{ flex: 1, overflow: 'hidden' }}>
                    <ChatMessages
                      messages={displayedMessages}
                      chatRoomId={activeChatRoom._id}
                      onSendMessage={handleSendMessage}
                      onMarkAsRead={handleMarkAsRead}
                      onLoadMore={handleLoadMoreMessages}
                      isLoadingMore={isLoadingMoreMessages}
                      hasMore={hasMoreMessages}
                    />
                  </Box>
                </>
              ) : isLoadingChat ? (
                <ChatLoader />
              ) : (
                <ChatPlaceholder />
              )}
            </Box>
          </Allotment.Pane>
        </Allotment>
      </Box>

      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: { xs: 'flex', sm: 'none' },
          flexDirection: 'column',
        }}
      >
        <Toolbar sx={{ px: 2, minHeight: '48px !important', justifyContent: 'space-between' }}>
          <IconButton color="inherit" aria-label="open drawer" edge="start" onClick={handleDrawerToggle}>
            <MenuIcon />
          </IconButton>
        </Toolbar>

        <Paper
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            height: 'calc(100vh - 148px)',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          {activeChatRoom ? (
            <ChatMessages
              messages={displayedMessages}
              chatRoomId={activeChatRoom._id}
              onSendMessage={handleSendMessage}
              onMarkAsRead={handleMarkAsRead}
              onLoadMore={handleLoadMoreMessages}
              isLoadingMore={isLoadingMoreMessages}
              hasMore={hasMoreMessages}
            />
          ) : isLoadingChat ? (
            <ChatLoader />
          ) : (
            <ChatPlaceholder />
          )}
        </Paper>
      </Box>

      {/* Contact picker for mobile compose */}
      <ContactPickerModal
        open={mobileContactPickerOpen}
        onClose={() => setMobileContactPickerOpen(false)}
        staffs={staffs || []}
        onContactSelect={(id) => {
          setMobileContactPickerOpen(false)
          handleStartNewChat(id)
        }}
        currentUserId={authUser?._id!}
        existingPrivateChats={privateChats || []}
        onLoadMore={handleLoadMoreContacts}
        isLoadingMore={isLoadingMoreContacts}
        hasMore={hasMoreContacts}
      />

      {/* Group dialog for mobile compose */}
      <CreateGroupChat creatorId={authUser?._id!} staffs={staffs || []} hideTrigger open={mobileGroupOpen} onOpenChange={setMobileGroupOpen} />
    </Box>
  )
}

export default ChatPage
